import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  Slide,
  Paper,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Call,
  CallEnd,
  Videocam,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface ActiveHuddle {
  channelId: string;
  channelName: string;
  participants: {
    userId: string;
    name: string;
    avatar?: string;
    isVideoEnabled: boolean;
    isAudioEnabled: boolean;
  }[];
  startedBy: string;
  startedAt: Date;
}

interface IncomingCallNotificationProps {
  onJoinHuddle?: (channelId: string, channelName: string) => void;
  onOpenChat?: (channelId: string) => void;
}

// Slide up transition
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const IncomingCallNotification: React.FC<IncomingCallNotificationProps> = ({
  onJoinHuddle,
  onOpenChat,
}) => {
  const { state: authState } = useAuth();
  const [activeHuddles, setActiveHuddles] = useState<ActiveHuddle[]>([]);
  const [dismissedHuddles, setDismissedHuddles] = useState<Set<string>>(new Set());
  const [isRinging, setIsRinging] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Get API base URL
  const getApiUrl = () => {
    return process.env.NODE_ENV === 'production' 
      ? window.location.origin 
      : `http://${window.location.hostname}:3002`;
  };

  // Poll for active huddles in user's channels
  const pollForActiveHuddles = useCallback(async () => {
    if (!authState.user?.id || !authState.token) return;

    try {
      const apiUrl = getApiUrl();
      
      // Get user's chat channels first
      const channelsResponse = await fetch(`${apiUrl}/api/chat/channels/${authState.user.id}`, {
        headers: {
          'Authorization': `Bearer ${authState.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!channelsResponse.ok) return;
      
      const channelsData = await channelsResponse.json();
      const channels = channelsData.channels || [];

      // Check each channel for active huddles
      const huddles: ActiveHuddle[] = [];
      
      for (const channel of channels) {
        try {
          const statusResponse = await fetch(`${apiUrl}/api/huddle-polling/status/${channel.id}`, {
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            
            // If there are participants and current user is NOT in the huddle
            if (statusData.exists && statusData.participantCount > 0) {
              const isUserInHuddle = statusData.participants.some(
                (p: any) => p.userId === authState.user?.id
              );

              if (!isUserInHuddle) {
                huddles.push({
                  channelId: channel.id,
                  channelName: channel.name || 'Huddle',
                  participants: statusData.participants,
                  startedBy: statusData.participants[0]?.name || 'Someone',
                  startedAt: new Date(statusData.createdAt),
                });
              }
            }
          }
        } catch (error) {
          // Ignore individual channel errors
        }
      }

      // Filter out dismissed huddles
      const newHuddles = huddles.filter(h => !dismissedHuddles.has(h.channelId));
      
      // Check if we have new huddles to ring for
      const hadHuddlesBefore = activeHuddles.length > 0;
      const hasHuddlesNow = newHuddles.length > 0;
      
      // Start ringing if new huddles appeared
      if (!hadHuddlesBefore && hasHuddlesNow) {
        startRinging();
      }
      
      // Stop ringing if no more huddles
      if (hadHuddlesBefore && !hasHuddlesNow) {
        stopRinging();
      }

      setActiveHuddles(newHuddles);
    } catch (error) {
      console.error('Error polling for huddles:', error);
    }
  }, [authState.user?.id, authState.token, dismissedHuddles, activeHuddles.length]);

  // Start ringing sound
  const startRinging = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.loop = true;
      audioRef.current.volume = 0.5;
      audioRef.current.play().catch(console.error);
    }
    setIsRinging(true);
  }, []);

  // Stop ringing sound
  const stopRinging = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsRinging(false);
  }, []);

  // Accept call - join the huddle
  const handleAccept = useCallback((huddle: ActiveHuddle) => {
    stopRinging();
    setDismissedHuddles(prev => {
      const newSet = new Set(Array.from(prev));
      newSet.add(huddle.channelId);
      return newSet;
    });
    
    if (onJoinHuddle) {
      onJoinHuddle(huddle.channelId, huddle.channelName);
    } else if (onOpenChat) {
      onOpenChat(huddle.channelId);
    }
  }, [stopRinging, onJoinHuddle, onOpenChat]);

  // Decline call - dismiss notification
  const handleDecline = useCallback((huddle: ActiveHuddle) => {
    stopRinging();
    setDismissedHuddles(prev => {
      const newSet = new Set(Array.from(prev));
      newSet.add(huddle.channelId);
      return newSet;
    });
    setActiveHuddles((prev: ActiveHuddle[]) => prev.filter((h: ActiveHuddle) => h.channelId !== huddle.channelId));
  }, [stopRinging]);

  // Start polling on mount
  useEffect(() => {
    // Initial poll
    pollForActiveHuddles();

    // Poll every 3 seconds
    pollingRef.current = setInterval(pollForActiveHuddles, 3000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      stopRinging();
    };
  }, [pollForActiveHuddles, stopRinging]);

  // Clear dismissed huddles after 5 minutes (allow re-notification)
  useEffect(() => {
    const cleanup = setInterval(() => {
      setDismissedHuddles(new Set());
    }, 300000); // 5 minutes

    return () => clearInterval(cleanup);
  }, []);

  // Don't render if no active huddles
  if (activeHuddles.length === 0) {
    return null;
  }

  const currentHuddle = activeHuddles[0]; // Show first huddle

  return (
    <>
      {/* Hidden audio element for ringtone */}
      <audio
        ref={audioRef}
        src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"
        preload="auto"
      />

      {/* Incoming call notification */}
      <Dialog
        open={activeHuddles.length > 0}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => handleDecline(currentHuddle)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.1)',
            position: 'fixed',
            bottom: 24,
            right: 24,
            left: 'auto',
            top: 'auto',
            m: 0,
            width: 340,
            animation: isRinging ? 'shake 0.5s ease-in-out infinite' : 'none',
            '@keyframes shake': {
              '0%, 100%': { transform: 'translateX(0)' },
              '25%': { transform: 'translateX(-5px)' },
              '75%': { transform: 'translateX(5px)' },
            },
          },
        }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'transparent' },
          },
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          {/* Caller info */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                mb: 2,
              }}
            >
              <Avatar
                src={currentHuddle.participants[0]?.avatar}
                sx={{
                  width: 80,
                  height: 80,
                  fontSize: '2rem',
                  backgroundColor: '#4caf50',
                  animation: isRinging ? 'pulse 1s ease-in-out infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)' },
                    '50%': { boxShadow: '0 0 0 20px rgba(76, 175, 80, 0)' },
                  },
                }}
              >
                {currentHuddle.startedBy.charAt(0).toUpperCase()}
              </Avatar>
              <Videocam
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  backgroundColor: '#4caf50',
                  borderRadius: '50%',
                  padding: 0.5,
                  fontSize: 24,
                  color: 'white',
                }}
              />
            </Box>

            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              {currentHuddle.startedBy}
            </Typography>
            
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              vill prata i
            </Typography>
            
            <Typography variant="body1" sx={{ color: '#4caf50', fontWeight: 500 }}>
              #{currentHuddle.channelName}
            </Typography>

            {currentHuddle.participants.length > 1 && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 1 }}>
                +{currentHuddle.participants.length - 1} andra i samtalet
              </Typography>
            )}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <IconButton
                onClick={() => handleDecline(currentHuddle)}
                sx={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  width: 56,
                  height: 56,
                  '&:hover': {
                    backgroundColor: '#d32f2f',
                  },
                }}
              >
                <CallEnd sx={{ fontSize: 28 }} />
              </IconButton>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 1 }}>
                Avvisa
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <IconButton
                onClick={() => handleAccept(currentHuddle)}
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  width: 56,
                  height: 56,
                  animation: 'bounce 0.6s ease-in-out infinite',
                  '@keyframes bounce': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.1)' },
                  },
                  '&:hover': {
                    backgroundColor: '#388e3c',
                  },
                }}
              >
                <Call sx={{ fontSize: 28 }} />
              </IconButton>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 1 }}>
                Svara
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Show count if multiple huddles */}
      {activeHuddles.length > 1 && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 200,
            right: 24,
            backgroundColor: 'rgba(30, 30, 30, 0.9)',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2">
            +{activeHuddles.length - 1} fler inkommande samtal
          </Typography>
        </Paper>
      )}
    </>
  );
};

export default IncomingCallNotification;
